import qs from "qs";
import { lazy, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Navigate, Outlet, Route, Routes, useLocation } from "react-router";
import { isAuthenticatedSelector, isUserDataCompleteSelector, userSelector } from "store/auth";
import { globalsSelector } from "store/globals";
import { segmentsSelector } from "store/segments";
import { uiSelector } from "store/ui";
import {
	getLaboratoryAndImagingSegmentFromSegments,
	getMedicalSegmentFromSegments,
	getPatientSegmentFromSegments,
	getPharmacySegmentFromSegments,
	getRouteLocale,
	isCountryEgypt,
	isDefaultLocale,
	isHospitalCategory,
	isNotesPatientQuestionnairePath,
	isObject,
	isSubdomain,
	shouldRestrictIsolateMembers,
} from "utils/helpers";
import { shouldShowIframeAccessDenied } from "utils/helpers/iframe-access";
import { getTeamModeratorRedirectPathname } from "utils/helpers/team-moderator-access";
import { ABILITIES, can } from "utils/helpers/abilities";
import vars from "utils/vars";
import IframeAccessDenied from "views/components/IframeAccessDenied";
import { default as ErrPage } from "views/pages/Err";
import PublicPatientIntakePublicRouteGate from "views/router/PublicPatientIntakePublicRouteGate";

const AboutUsPage = lazy(() => import("views/pages/AboutUs"));
const AccountInformationForm = lazy(() => import("views/forms/AccountInformation"));
const AccountPasswordForm = lazy(() => import("views/forms/AccountPassword"));
const AccountPaymentMethods = lazy(() => import("views/sections/PaymentMethodsList"));
const AppointmentsListing = lazy(() => import("views/sections/AppointmentsListing"));
const AppointmentsPage = lazy(() => import("views/pages/user/Appointments"));
const AppointmentsSingle = lazy(() => import("views/sections/AppointmentsSingle"));
const BlogListingPage = lazy(() => import("views/pages/blog/BlogListing"));
const BlogSinglePage = lazy(() => import("views/pages/blog/BlogSingle"));
const BranchesFormPage = lazy(() => import("views/pages/user/branches/BranchesForm"));
const BranchesListingPage = lazy(() => import("views/pages/user/branches/BranchesListing"));
const CareersPage = lazy(() => import("views/pages/Careers"));
const CaseQuestionForm = lazy(() => import("views/sections/CaseQuestionForm"));
const CasesPage = lazy(() => import("views/pages/Cases"));
const CaseStudyPage = lazy(() => import("views/pages/CaseStudy"));
const ConnectionsList = lazy(() => import("views/sections/ConnectionsList"));
const ConnectionsPage = lazy(() => import("views/pages/user/Connections"));
const ContactUsPage = lazy(() => import("views/pages/support/ContactUs"));
const DoctorsPage = lazy(() => import("views/pages/Doctors"));
const DocumentsListing = lazy(() => import("views/sections/DocumentsListing"));
const DocumentsPage = lazy(() => import("views/pages/user/Documents"));
const ElectronicFormOrderPdf = lazy(() => import("views/pages/Pdf"));
const ElectronicFormOrders = lazy(() => import("views/components/ElectronicFormOrders"));
const EmailVerificationPage = lazy(() => import("views/pages/user/EmailVerification"));
const FinishDemoPage = lazy(() => import("views/pages/demo/Finish"));
const ForgotPasswordConfirmationPage = lazy(() => import("views/pages/auth/ForgotPasswordConfirmation"));
const ForgotPasswordPage = lazy(() => import("views/pages/auth/ForgotPassword"));
const HomePage = lazy(() => import("views/pages/Home"));
const InformationEditPage = lazy(() => import("views/pages/user/InformationEdit"));
const LabAndImagingDrafts = lazy(() => import("views/sections/LabAndImagingDrafts"));
const LabAndImagingDraftsOrders = lazy(() => import("views/sections/LabAndImagingDraftsOrders"));
const LabAndImagingDraftsReferrals = lazy(() => import("views/sections/LabAndImagingDraftsReferrals"));
const LabAndImagingOrdersSubmissions = lazy(() => import("views/sections/LabAndImagingOrdersSubmissions"));
const LabAndImagingPage = lazy(() => import("views/pages/user/LabAndImaging"));
const LabAndImagingReferrals = lazy(() => import("views/sections/LabAndImagingReferrals"));
const LaboratoryAndImagingPage = lazy(() => import("views/pages/LaboratoryAndImaging"));
const LoginAsPage = lazy(() => import("views/pages/LoginAs"));
const LoginPage = lazy(() => import("views/pages/auth/Login"));
const MaintenancePage = lazy(() => import("views/pages/Maintenance"));
const MedicalHistoryPage = lazy(() => import("views/pages/user/MedicalHistory"));
const MobileVerificationPage = lazy(() => import("views/pages/user/MobileVerification"));
const NewsletterPage = lazy(() => import("views/pages/Newsletter"));
const NoteAssessment = lazy(() => import("views/sections/NoteAssessment"));
const NoteFinal = lazy(() => import("views/sections/NoteFinal"));
const NoteObjective = lazy(() => import("views/sections/NoteObjective"));
const NotePlan = lazy(() => import("views/sections/NotePlan"));
const NotesDetailsPage = lazy(() => import("views/pages/notes/NotesDetails"));
const NotesListingPage = lazy(() => import("views/pages/notes/NotesListing"));
const NotesPatientFinishPage = lazy(() => import("views/sections/NotesPatientFinish"));
const NotesPatientPage = lazy(() => import("views/pages/notes/NotesPatient"));
const NotesPatientQuestionsPage = lazy(() => import("views/sections/NotesPatientQuestions"));
const NotesPatientValidationPage = lazy(() => import("views/sections/NotesPatientValidation"));
const NotesPrintPrescription = lazy(() => import("views/sections/NotesPrintPrescription"));
const NotesAllDocuments = lazy(() => import("views/sections/NotesAllDocuments"));
const NotesReferralPage = lazy(() => import("views/pages/notes/NotesReferral"));
const NotesReferralPrintPage = lazy(() => import("views/pages/notes/NotesReferralPrint"));
const NotesSingleInfo = lazy(() => import("views/pages/notes/NotesSingleInfo"));
const NotesSingleLogs = lazy(() => import("views/sections/NotesSingleLogs"));
const NotesSinglePage = lazy(() => import("views/pages/notes/NotesSingle"));
const NotesStorePage = lazy(() => import("views/pages/notes/NotesStore"));
const NoteSubjective = lazy(() => import("views/sections/NoteSubjective"));
const NotificationsPage = lazy(() => import("views/pages/user/Notifications"));
const OrderForm = lazy(() => import("views/sections/OrderForm"));
const OrdersPage = lazy(() => import("views/pages/user/electronic-form/Orders"));
const PackagesListingPage = lazy(() => import("views/pages/packages/PackagesListing"));
const PatientAppointmentForm = lazy(() => import("views/sections/PatientAppointmentForm"));
const PatientForm = lazy(() => import("views/sections/PatientForm"));
const PatientFormForm = lazy(() => import("views/forms/PatientForm"));
const PatientIntakeBookingFee = lazy(() => import("views/sections/PatientIntakeBookingFee"));
const PatientIntakeSourceQrCodes = lazy(() => import("views/sections/PatientIntakeSourceQrCodes"));
const PatientIntakeTransactions = lazy(() => import("views/sections/PatientIntakeTransactions"));
const PatientIntakeCases = lazy(() => import("views/sections/PatientIntakeCases"));
const PatientIntakeList = lazy(() => import("views/sections/PatientIntakeList"));
const PatientIntakePage = lazy(() => import("views/pages/user/PatientIntake"));
const PatientIntakeSchedule = lazy(() => import("views/sections/PatientIntakeSchedule"));
const PatientIntakePreBookingConsents = lazy(() => import("views/sections/PatientIntakePreBookingConsents"));
const PatientList = lazy(() => import("views/sections/PatientList"));
const PatientPage = lazy(() => import("views/pages/user/Patient"));
const PatientSingle = lazy(() => import("views/sections/PatientSingle"));
const PatientSingleHistory = lazy(() => import("views/sections/PatientSingleHistory"));
const PatientSingleInfo = lazy(() => import("views/sections/PatientSingleInfo"));
const PatientSingleLabOrders = lazy(() => import("views/sections/PatientSingleLabOrders"));
const PatientSingleNotesListing = lazy(() => import("views/sections/PatientSingleNotesListing"));
const PatientSingleReferrals = lazy(() => import("views/sections/PatientSingleReferrals"));
const PaymentHistory = lazy(() => import("views/components/PaymentHistory"));
const PaymentMethod = lazy(() => import("views/sections/PaymentMethod"));
const PharmacistsPage = lazy(() => import("views/pages/Pharmacists"));
const PricingPage = lazy(() => import("views/pages/Pricing"));
const PublicPatientIntakeCases = lazy(() => import("views/sections/PublicPatientIntakeCases"));
const PublicPatientIntakeChoose = lazy(() => import("views/sections/PublicPatientIntakeChoose"));
const PublicPatientIntakePatient = lazy(() => import("views/sections/PublicPatientIntakePatient"));
const PublicPatientIntakePayment = lazy(() => import("views/sections/PublicPatientIntakePayment"));
const PublicPatientIntakePaymentReturn = lazy(() => import("views/sections/PublicPatientIntakePaymentReturn"));
const PublicPatientIntakeProfile = lazy(() => import("views/sections/PublicPatientIntakeProfile"));
const PublicPatientIntakeQuestions = lazy(() => import("views/sections/PublicPatientIntakeQuestions"));
const PublicPatientIntakeStart = lazy(() => import("views/sections/PublicPatientIntakeStart"));
const PublicPatientIntakeStore = lazy(() => import("views/sections/PublicPatientIntakeStore"));
const RegisterPage = lazy(() => import("views/pages/auth/Register"));
const ReportsAndPrescriptionsPage = lazy(() => import("views/pages/user/ReportsAndPrescriptions"));
const ReportsAndPrescriptionsShared = lazy(() => import("views/sections/ReportsAndPrescriptionsShared"));
const ResetPasswordPage = lazy(() => import("views/pages/auth/ResetPassword"));
const SetPasswordPage = lazy(() => import("views/pages/auth/SetPassword"));
const SettingsAppointmentDuration = lazy(() => import("views/sections/AppointmentDuration"));
const SettingsLanguage = lazy(() => import("views/sections/SettingsLanguage"));
const SettingsList = lazy(() => import("views/sections/SettingsList"));
const SettingsVirtualAssessment = lazy(() => import("views/sections/VirtualAssessment"));
const SettingsOutput = lazy(() => import("views/sections/SettingsOutput"));
const SettingsPage = lazy(() => import("views/pages/user/Settings"));
const SettingsRecognizedDevices = lazy(() => import("views/sections/RecognizedDevices"));
const SettingsShareQuestionnaire = lazy(() => import("views/sections/ShareQuestionnaire"));
const SignPage = lazy(() => import("views/pages/user/Sign"));
const StartDemoPage = lazy(() => import("views/pages/demo/Start"));
const StaticPage = lazy(() => import("views/pages/StaticPage"));
const SubscriptionsExtraPage = lazy(() => import("views/pages/subscriptions/SubscriptionsExtra"));
const SubscriptionsOneTimePage = lazy(() => import("views/pages/subscriptions/SubscriptionsOneTime"));
const TasksListing = lazy(() => import("views/sections/TasksListing"));
const TasksPage = lazy(() => import("views/pages/user/Tasks"));
const TasksSingle = lazy(() => import("views/sections/TasksSingle"));
const UpdatePasswordPage = lazy(() => import("views/pages/auth/UpdatePassword"));
const UserCasesFormSection = lazy(() => import("views/sections/CustomizeCasesForm"));
const UserCasesListingSection = lazy(() => import("views/sections/CustomizeCasesListing"));
const UserCasesPage = lazy(() => import("views/pages/user/Cases"));
const UserHomePage = lazy(() => import("views/pages/user/Home"));
const UserPatientInfo = lazy(() => import("views/sections/UserPatientInfo"));
const UserSubscriptionInvoicePage = lazy(() => import("views/pages/user/Invoice"));
const UserSubscriptionPage = lazy(() => import("views/pages/user/Subscription"));
const UserTeamFormPage = lazy(() => import("views/pages/user/team/TeamForm"));
const UserTeamListing = lazy(() => import("views/sections/UserTeamListing"));
const UserTeamListingPage = lazy(() => import("views/pages/user/team/TeamListing"));
const UserJoinRequestsPage = lazy(() => import("views/pages/user/join-requests/JoinRequestsPage"));
const WebinarPage = lazy(() => import("views/pages/Webinar"));
const WellHealthIntegration = lazy(() => import("views/forms/WellHealthIntegration"));
const WellHealthLegacyCallbackRedirect = lazy(() =>
	import("views/forms/WellHealthIntegration/WellHealthLegacyCallbackRedirect")
);
const WellHealthOAuthCallback = lazy(() => import("views/forms/WellHealthIntegration/WellHealthOAuthCallback"));

const Router = ({ isMaintenanceMode }) => {
	const { i18n, t } = useTranslation();
	const { pathname, search, hash } = useLocation();
	const {
		redirect_to = "/",
		otp: questionnaireOtpFromSearch,
		...restOfQueryParams
	} = qs.parse(search, {
		ignoreQueryPrefix: true,
	});

	// redux global state
	const globals = useSelector(globalsSelector);
	const user = useSelector(userSelector);
	const isAuthenticated = useSelector(isAuthenticatedSelector);
	const isUserDataComplete = useSelector(isUserDataCompleteSelector);
	const segments = useSelector(segmentsSelector);
	const ui = useSelector(uiSelector);

	// memo hooks
	const medicalSegment = useMemo(() => getMedicalSegmentFromSegments(segments), [segments]);
	const pharmacySegment = useMemo(() => getPharmacySegmentFromSegments(segments), [segments]);
	const patientSegment = useMemo(() => getPatientSegmentFromSegments(segments), [segments]);
	const laboratoryAndImagingSegment = useMemo(() => getLaboratoryAndImagingSegmentFromSegments(segments), [segments]);
	const canAccessSharedQuestionnaireNote = useMemo(() => {
		if (!isNotesPatientQuestionnairePath(pathname)) return false;
		if (String(questionnaireOtpFromSearch ?? "").length === 4) return true;
		return /\/notes\/p\/[^/]+\/(questions|finish)\/?$/i.test(pathname);
	}, [pathname, questionnaireOtpFromSearch]);

	const isCurrentCategoryHospitalCategory = useMemo(
		() => isHospitalCategory(user?.segment_category),
		[user?.segment_category]
	);
	const isSubdomainMemo = useMemo(() => isSubdomain(), []);
	const isCurrentSegmentMedicalSegment = medicalSegment?.id === globals?.segment?.id || false;
	const isCurrentSegmentPharmacySegment = pharmacySegment?.id === globals?.segment?.id || false;
	const isCurrentSegmentLaboratoryAndImagingSegment =
		laboratoryAndImagingSegment?.id === globals?.segment?.id || false;
	const isCurrentSegmentPatientSegment = patientSegment?.id === globals?.segment?.id || false;
	const isDemoTenant = Boolean(user?.tenant?.is_demo);
	const isUserSigned = user?.signed || false;
	// the subscription and payment pages stay the Account Holder's; an Account Manager runs the rest
	const isUserOwner = can(user, ABILITIES.manageBilling);
	const canManageAccount = can(user, ABILITIES.manageAccount);
	const canManageTeam = can(user, ABILITIES.manageTeam);
	const isWellHealthIntegrationEnabled = vars.app.allowWellHealthIntegration && canManageAccount;
	const isVirtualAssessmentSettingAllowed =
		Boolean(globals?.tenant_info?.admin_allow_virtual_assessment) && canManageAccount;
	const canManageJoinRequests =
		Boolean(globals?.tenant_info?.virtual_assessment) &&
		isCurrentSegmentPharmacySegment &&
		(canManageTeam || user?.can_create_members);
	const isUserHasSubscription = user?.subscription?.id || false;
	const isUserEmailVerified = user?.status?.email_verified || false;
	const isUserMobileVerified = user?.status?.mobile_verified || false;
	const isUserTypeProfessional = user?.type === vars.app.usersTypes.professional;
	const isUserTypeAssociate = user?.type === vars.app.usersTypes.associate;
	const isUserTeamModerator = user?.position === vars.app.positions.teamModerator;
	// members carry the account holder's subscription without its id, so an Account Manager or Team
	// Moderator never satisfies isUserHasSubscription on the team pages
	const hasTeamPageSubscription = isUserHasSubscription || (canManageTeam && Boolean(user?.subscription));
	const isIsolateMembersRestricted = shouldRestrictIsolateMembers(globals, user);

	const isUserHasUnlimitedNotes = Boolean(+user?.subscription?.usage?.notes?.total < 0);
	const isUserReachesHisNotesLimit = Boolean(
		!isUserHasUnlimitedNotes && +user?.subscription?.usage?.notes?.used >= +user?.subscription?.usage?.notes?.total
	);
	const isRecurringSubscription = Boolean(+user?.subscription?.recurring_notes || 0);
	const isEgyptianGuest =
		(isAuthenticated && isCountryEgypt(user?.country)) ||
		(!isAuthenticated && globals?.client_location?.is_egypt) ||
		false;
	const isUserFirstTimeLogin = user?.is_first_login || false;
	const redirectToFullPath = `${pathname}${
		isObject(restOfQueryParams) && `?${qs.stringify(restOfQueryParams)}`
	}${hash}`;
	const isTaskManagementEnabled = Boolean(+globals?.segment?.enable_tasks_management) || false;
	const isNotificationsEnabled = Boolean(globals?.segment?.enable_notification) || false;
	const isGlobalLoaded = JSON.stringify(globals) !== JSON.stringify({});
	const isUserInSubscriptionFreeTrial = Boolean(+user?.subscription?.free_trial) || false;
	const isUserHasPatientIntake = Boolean(+user?.has_patient_intake) || false;
	// a Team Moderator manages the clinic's Source QR codes, the only Patient Intake tab they can open
	const canTeamModeratorManageSourceQrCodes =
		isUserTeamModerator &&
		Boolean(globals?.tenant_info?.source_qr_codes_available) &&
		!isDemoTenant &&
		isUserSigned &&
		!isCurrentSegmentLaboratoryAndImagingSegment &&
		(isUserInSubscriptionFreeTrial || isUserHasPatientIntake);

	if (shouldShowIframeAccessDenied(pathname, i18n.language)) {
		return <IframeAccessDenied />;
	}

	// a Team Moderator may only use the team pages, their own profile/password pages and, where
	// the clinic has them, the Source QR codes tab
	const teamModeratorRedirectPathname =
		isUserDataComplete && isUserTeamModerator
			? getTeamModeratorRedirectPathname(pathname, i18n.language, {
					canManageSourceQrCodes: canTeamModeratorManageSourceQrCodes,
			  })
			: null;
	if (teamModeratorRedirectPathname) {
		return <Navigate to={`${getRouteLocale(i18n.language)}${teamModeratorRedirectPathname}`} replace />;
	}

	return (
		<Routes>
			<Route path={getRouteLocale(i18n.language) || "/"}>
				{!isMaintenanceMode && (
					<>
						<Route
							element={
								!isUserDataComplete && !isSubdomainMemo ? (
									<HomePage pageTitle={t("titles.home")} />
								) : (
									<Navigate
										to={`${getRouteLocale(i18n.language)}${
											!isUserDataComplete
												? `/auth/login?${qs.stringify({ redirect_to: redirectToFullPath })}`
												: ui?.uiUserPasswordUpdateNeeded
												? `/auth/password/update?${qs.stringify({
														...(!user?.terms_conditions_accept && {
															terms_conditions_accept: 0,
														}),
												  })}`
												: isDemoTenant
												? "/demo"
												: !isUserSigned
												? "/user/sign"
												: isUserTeamModerator
												? "/user/team"
												: !isUserHasSubscription && isUserOwner
												? "/packages"
												: isUserFirstTimeLogin && isCurrentSegmentLaboratoryAndImagingSegment
												? "/user/electronic-form/orders"
												: user?.login_with_tenant
												? isCurrentSegmentMedicalSegment &&
												  (isUserTypeProfessional || isUserTypeAssociate)
													? "/cases"
													: "/user/me"
												: "/user/me"
										}`}
										replace
									/>
								)
							}
							index
						/>
						<Route path="public" element={<Navigate to={getRouteLocale(i18n.language)} replace />} />
						{!isEgyptianGuest && (
							<Route
								path="pharmacists"
								element={
									!isAuthenticated && !isSubdomainMemo ? (
										<PharmacistsPage
											pageTitle={
												getPharmacySegmentFromSegments(segments)?.title || t("titles.pharmacy")
											}
										/>
									) : (
										<Navigate
											to={`${getRouteLocale(i18n.language)}${
												!isAuthenticated
													? `/auth/login?${qs.stringify({ redirect_to: redirectToFullPath })}`
													: "/"
											}`}
											replace
										/>
									)
								}
							/>
						)}
						<Route
							path="doctors"
							element={
								!isAuthenticated && !isSubdomainMemo ? (
									<DoctorsPage
										pageTitle={
											getMedicalSegmentFromSegments(segments)?.title || t("titles.medical")
										}
									/>
								) : (
									<Navigate
										to={`${getRouteLocale(i18n.language)}${
											!isAuthenticated
												? `/auth/login?${qs.stringify({ redirect_to: redirectToFullPath })}`
												: "/"
										}`}
										replace
									/>
								)
							}
						/>
						<Route
							path="laboratory-and-imaging"
							element={
								!isAuthenticated && !isSubdomainMemo ? (
									<LaboratoryAndImagingPage
										pageTitle={
											getLaboratoryAndImagingSegmentFromSegments(segments)?.title ||
											t("titles.lab_and_imaging")
										}
									/>
								) : (
									<Navigate
										to={`${getRouteLocale(i18n.language)}${
											!isAuthenticated
												? `/auth/login?${qs.stringify({ redirect_to: redirectToFullPath })}`
												: "/"
										}`}
										replace
									/>
								)
							}
						/>
						<Route
							path="webinar"
							element={
								!isAuthenticated && !isSubdomainMemo ? (
									<WebinarPage pageTitle={t("titles.webinar")} />
								) : (
									<Navigate
										to={`${getRouteLocale(i18n.language)}${
											!isAuthenticated
												? `/auth/login?${qs.stringify({ redirect_to: redirectToFullPath })}`
												: "/"
										}`}
										replace
									/>
								)
							}
						/>
						<Route
							path="case-study-01-accuracy-and-time-saving-in-clinical-note-documentation-using-TAPTYPE"
							element={<CaseStudyPage pageTitle={t("titles.case-study")} />}
						/>
						<Route path="auth">
							<Route element={<Navigate to={`${getRouteLocale(i18n.language)}/error`} replace />} index />
							<Route
								path="login"
								element={
									!isUserDataComplete ? (
										<LoginPage pageTitle={t("titles.login")} />
									) : (
										<Navigate to={redirect_to} replace />
									)
								}
							/>
							<Route
								path="register"
								element={
									!isAuthenticated || (isAuthenticated && isDemoTenant) ? (
										<RegisterPage pageTitle={t("titles.join_now")} />
									) : (
										<Navigate to={redirect_to} replace />
									)
								}
							/>
							<Route path="password">
								<Route
									element={<Navigate to={`${getRouteLocale(i18n.language)}/error`} replace />}
									index
								/>
								<Route path="forgot">
									<Route
										element={
											!isAuthenticated ? (
												<ForgotPasswordPage pageTitle={t("titles.forgot_password")} />
											) : (
												<Navigate to={redirect_to} replace />
											)
										}
										index
									/>
									<Route
										path="confirmation"
										element={
											!isAuthenticated ? (
												<ForgotPasswordConfirmationPage
													pageTitle={t("titles.forgot_password")}
												/>
											) : (
												<Navigate to={redirect_to} replace />
											)
										}
									/>
								</Route>
								<Route path="reset">
									<Route
										element={<Navigate to={`${getRouteLocale(i18n.language)}/error`} replace />}
										index
									/>
									<Route
										path=":token"
										element={<ResetPasswordPage pageTitle={t("titles.reset_password")} />}
									/>
								</Route>
								<Route path="set">
									<Route
										element={<Navigate to={`${getRouteLocale(i18n.language)}/error`} replace />}
										index
									/>
									<Route
										path=":token"
										element={<SetPasswordPage pageTitle={t("titles.set_password")} />}
									/>
								</Route>
								<Route
									path="update"
									element={
										isAuthenticated ? (
											ui?.uiUserPasswordUpdateNeeded ? (
												<UpdatePasswordPage pageTitle={t("titles.update_password")} />
											) : (
												<Navigate to={redirect_to} replace />
											)
										) : (
											<Navigate
												to={`${getRouteLocale(i18n.language)}/auth/login?${qs.stringify({
													redirect_to: redirectToFullPath,
												})}`}
												replace
											/>
										)
									}
								/>
							</Route>
						</Route>
						<Route path="loginAs">
							<Route element={<Navigate to={`${getRouteLocale(i18n.language)}/error`} replace />} index />
							<Route path=":key" element={<LoginAsPage pageTitle={t("titles.login_as")} />} />
						</Route>
						<Route
							path="packages"
							element={
								isAuthenticated ? (
									!isDemoTenant && isUserOwner && isUserSigned && !isUserHasSubscription ? (
										<PackagesListingPage pageTitle={t("titles.packages")} />
									) : (
										<Navigate to={redirect_to} replace />
									)
								) : (
									<Navigate
										to={`${getRouteLocale(i18n.language)}/auth/login?${qs.stringify({
											redirect_to: redirectToFullPath,
										})}`}
										replace
									/>
								)
							}
						/>
						<Route path="subscriptions">
							<Route element={<Navigate to={`${getRouteLocale(i18n.language)}/error`} replace />} index />
							<Route
								path="extra"
								element={
									isAuthenticated ? (
										!isDemoTenant && isUserSigned && isUserOwner ? (
											<SubscriptionsExtraPage
												pageTitle={
													isRecurringSubscription
														? t("titles.edit_notes_bundle")
														: isCurrentSegmentLaboratoryAndImagingSegment
														? t("titles.add_orders_bundle")
														: t("titles.add_notes_bundle")
												}
											/>
										) : (
											<Navigate to={redirect_to} replace />
										)
									) : (
										<Navigate
											to={`${getRouteLocale(i18n.language)}/auth/login?${qs.stringify({
												redirect_to: redirectToFullPath,
											})}`}
											replace
										/>
									)
								}
							/>
							<Route
								path="one-time"
								element={
									!isAuthenticated && !isSubdomainMemo ? (
										<SubscriptionsOneTimePage pageTitle="Membership details" />
									) : (
										<Navigate to={redirect_to} replace />
									)
								}
							/>
						</Route>
						<Route
							path="cases"
							element={
								isAuthenticated ? (
									!isDemoTenant &&
									(!isGlobalLoaded || (isGlobalLoaded && isCurrentSegmentMedicalSegment)) &&
									(isUserTypeProfessional || isUserTypeAssociate) &&
									isUserSigned ? (
										<CasesPage pageTitle={t("titles.cases")} />
									) : (
										<Navigate to={redirect_to} replace />
									)
								) : (
									<Navigate
										to={`${getRouteLocale(i18n.language)}/auth/login?${qs.stringify({
											redirect_to: redirectToFullPath,
										})}`}
										replace
									/>
								)
							}
						/>
						<Route path="user">
							<Route element={<Navigate to={`${getRouteLocale(i18n.language)}/error`} replace />} index />
							<Route
								path="sign"
								element={
									isAuthenticated ? (
										!isDemoTenant ? (
											!isUserSigned ? (
												<SignPage pageTitle={t("titles.terms_conditions_sign")} />
											) : (
												<Navigate to={`${getRouteLocale(i18n.language)}/user/me`} replace />
											)
										) : (
											<Navigate to={redirect_to} replace />
										)
									) : (
										<Navigate
											to={`${getRouteLocale(i18n.language)}/auth/login?${qs.stringify({
												redirect_to: redirectToFullPath,
											})}`}
											replace
										/>
									)
								}
							/>
							<Route path="email">
								<Route
									element={<Navigate to={`${getRouteLocale(i18n.language)}/error`} replace />}
									index
								/>
								<Route path="verify">
									<Route
										element={
											isAuthenticated ? (
												!isDemoTenant ? (
													isUserSigned && !isUserEmailVerified ? (
														<EmailVerificationPage
															pageTitle={t("titles.email_verification")}
														/>
													) : (
														<Navigate
															to={`${getRouteLocale(i18n.language)}/${
																!isUserHasSubscription && isUserOwner ? "packages" : ""
															}`}
															replace
														/>
													)
												) : (
													<Navigate to={redirect_to} replace />
												)
											) : (
												<Navigate
													to={`${getRouteLocale(i18n.language)}/auth/login?${qs.stringify({
														redirect_to: redirectToFullPath,
													})}`}
													replace
												/>
											)
										}
										index
									/>
									<Route
										path=":token"
										element={
											isAuthenticated ? (
												!isDemoTenant ? (
													isUserSigned && !isUserEmailVerified ? (
														<EmailVerificationPage
															pageTitle={t("titles.email_verification")}
														/>
													) : (
														<Navigate
															to={`${getRouteLocale(i18n.language)}/${
																!isUserHasSubscription && isUserOwner ? "packages" : ""
															}`}
															replace
														/>
													)
												) : (
													<Navigate to={redirect_to} replace />
												)
											) : (
												<Navigate
													to={`${getRouteLocale(i18n.language)}/auth/login?${qs.stringify({
														redirect_to: redirectToFullPath,
													})}`}
													replace
												/>
											)
										}
									/>
								</Route>
							</Route>
							<Route path="mobile">
								<Route
									element={<Navigate to={`${getRouteLocale(i18n.language)}/error`} replace />}
									index
								/>
								<Route path="verify">
									<Route
										element={
											isAuthenticated ? (
												!isDemoTenant ? (
													isUserSigned && !isUserMobileVerified ? (
														<MobileVerificationPage
															pageTitle={t("titles.mobile_verification")}
														/>
													) : (
														<Navigate
															to={`${getRouteLocale(i18n.language)}/${
																!isUserHasSubscription && isUserOwner ? "packages" : ""
															}`}
															replace
														/>
													)
												) : (
													<Navigate to={redirect_to} replace />
												)
											) : (
												<Navigate
													to={`${getRouteLocale(i18n.language)}/auth/login?${qs.stringify({
														redirect_to: redirectToFullPath,
													})}`}
													replace
												/>
											)
										}
										index
									/>
								</Route>
							</Route>
							<Route
								path="me"
								element={
									isAuthenticated ? (
										!isDemoTenant && isUserSigned ? (
											(isUserHasSubscription && isUserOwner) || !isUserOwner ? (
												<UserHomePage pageTitle={t("titles.home")} />
											) : (
												<Navigate to={`${getRouteLocale(i18n.language)}/packages`} replace />
											)
										) : (
											<Navigate to={redirect_to} replace />
										)
									) : (
										<Navigate
											to={`${getRouteLocale(i18n.language)}/auth/login?${qs.stringify({
												redirect_to: redirectToFullPath,
											})}`}
											replace
										/>
									)
								}
							/>
							<Route
								path="edit"
								element={
									isAuthenticated ? (
										!isDemoTenant && isUserSigned ? (
											(isUserHasSubscription && isUserOwner) || !isUserOwner ? (
												<InformationEditPage pageTitle={t("actions.profile")} />
											) : (
												<Navigate to={`${getRouteLocale(i18n.language)}/packages`} replace />
											)
										) : (
											<Navigate to={redirect_to} replace />
										)
									) : (
										<Navigate
											to={`${getRouteLocale(i18n.language)}/auth/login?${qs.stringify({
												redirect_to: redirectToFullPath,
											})}`}
											replace
										/>
									)
								}>
								<Route
									element={
										!isGlobalLoaded || (isGlobalLoaded && isCurrentSegmentPatientSegment) ? (
											<UserPatientInfo />
										) : (
											<Navigate
												to={`${getRouteLocale(i18n.language)}/user/edit/information`}
												replace
											/>
										)
									}
									index
								/>
								<Route
									path="information"
									element={
										!isGlobalLoaded || (isGlobalLoaded && isCurrentSegmentPatientSegment) ? (
											<PatientFormForm isLoggedInPatientProfileEditForm />
										) : (
											<AccountInformationForm />
										)
									}
								/>
								<Route path="password" element={<AccountPasswordForm />} />
								{isUserOwner && (
									<Route path="payment-methods">
										<Route element={<AccountPaymentMethods />} index />
										<Route path="store" element={<PaymentMethod />} />
										<Route path="edit">
											<Route
												element={
													<Navigate to={`${getRouteLocale(i18n.language)}/error`} replace />
												}
												index
											/>
											<Route path=":id" element={<PaymentMethod />} />
										</Route>
									</Route>
								)}
							</Route>
							{isWellHealthIntegrationEnabled && (
								<>
									<Route
										path="edit/well-health/callback"
										element={<WellHealthLegacyCallbackRedirect />}
									/>
									<Route
										path="edit/well-health"
										element={
											<Navigate
												to={`${getRouteLocale(i18n.language)}/user/settings/well-health`}
												replace
											/>
										}
									/>
								</>
							)}
							<Route
								path="cases"
								element={
									isAuthenticated ? (
										!isDemoTenant && isUserSigned ? (
											!isIsolateMembersRestricted &&
											((isUserHasSubscription && isUserOwner) ||
												isUserTypeProfessional ||
												isUserTypeAssociate) &&
											!isCurrentSegmentLaboratoryAndImagingSegment ? (
												<UserCasesPage pageTitle={t("titles.customize_cases")} />
											) : (
												<Navigate to={`${getRouteLocale(i18n.language)}/error`} replace />
											)
										) : (
											<Navigate to={redirect_to} replace />
										)
									) : (
										<Navigate
											to={`${getRouteLocale(i18n.language)}/auth/login?${qs.stringify({
												redirect_to: redirectToFullPath,
											})}`}
											replace
										/>
									)
								}>
								<Route element={<UserCasesListingSection />} index />
								{(!globals?.segment?.id || Boolean(globals?.segment?.create_case)) && (
									<Route path="store" element={<UserCasesFormSection />} />
								)}
								<Route path="edit">
									<Route path=":id" element={<UserCasesFormSection />} />
								</Route>
							</Route>
							<Route
								path="subscription"
								element={
									isAuthenticated ? (
										!isDemoTenant && isUserSigned ? (
											isUserHasSubscription && isUserOwner ? (
												<UserSubscriptionPage
													pageTitle={
														isCurrentSegmentLaboratoryAndImagingSegment
															? "My Plan"
															: t("titles.my_subscription")
													}
												/>
											) : (
												<Navigate to={`${getRouteLocale(i18n.language)}/packages`} replace />
											)
										) : (
											<Navigate to={redirect_to} replace />
										)
									) : (
										<Navigate
											to={`${getRouteLocale(i18n.language)}/auth/login?${qs.stringify({
												redirect_to: redirectToFullPath,
											})}`}
											replace
										/>
									)
								}>
								<Route
									element={
										<Navigate
											to={`${getRouteLocale(i18n.language)}/user/subscription${
												isCurrentSegmentLaboratoryAndImagingSegment ? "/history" : "/team"
											}`}
											replace
										/>
									}
									index
								/>
								{!isCurrentSegmentLaboratoryAndImagingSegment && (
									<Route
										path="team"
										element={<UserTeamListing type={vars.app.usersTypes.professional} />}
									/>
								)}
								<Route path="history" element={<PaymentHistory />} />
							</Route>
							<Route
								path="settings"
								element={
									isAuthenticated ? (
										!isDemoTenant && isUserSigned ? (
											<SettingsPage pageTitle={t("titles.settings")} />
										) : (
											<Navigate to={redirect_to} replace />
										)
									) : (
										<Navigate
											to={`${getRouteLocale(i18n.language)}/auth/login?${qs.stringify({
												redirect_to: redirectToFullPath,
											})}`}
											replace
										/>
									)
								}>
								<Route
									element={
										<Navigate to={`${getRouteLocale(i18n.language)}/user/settings/list`} replace />
									}
									index
								/>
								<Route path="list" element={<SettingsList />} />
								<Route path="output" element={<SettingsOutput />} />
								<Route path="language" element={<SettingsLanguage />} />
								<Route path="share-questionnaire" element={<SettingsShareQuestionnaire />} />
								<Route path="recognized-devices" element={<SettingsRecognizedDevices />} />
								<Route path="appointment-duration" element={<SettingsAppointmentDuration />} />
								{isVirtualAssessmentSettingAllowed && (
									<Route path="virtual-assessment" element={<SettingsVirtualAssessment />} />
								)}
								{isWellHealthIntegrationEnabled && (
									<Route path="well-health">
										<Route element={<WellHealthIntegration />} index />
										<Route path="callback" element={<WellHealthOAuthCallback />} />
									</Route>
								)}
							</Route>
							<Route path="team">
								<Route
									element={
										isAuthenticated ? (
											!isDemoTenant && isUserSigned ? (
												hasTeamPageSubscription ? (
													canManageTeam ? (
														<UserTeamListingPage pageTitle={t("titles.my_team")} />
													) : (
														<Navigate
															to={`${getRouteLocale(i18n.language)}/packages`}
															replace
														/>
													)
												) : (
													<Navigate to={`${getRouteLocale(i18n.language)}/error`} replace />
												)
											) : (
												<Navigate to={redirect_to} replace />
											)
										) : (
											<Navigate
												to={`${getRouteLocale(i18n.language)}/auth/login?${qs.stringify({
													redirect_to: redirectToFullPath,
												})}`}
												replace
											/>
										)
									}
									index
								/>
								<Route
									path="store"
									element={
										isAuthenticated ? (
											!isDemoTenant && isUserSigned ? (
												hasTeamPageSubscription && canManageTeam && user?.can_create_members ? (
													<UserTeamFormPage pageTitle={t("titles.my_team")} />
												) : (
													<Navigate
														to={`${getRouteLocale(i18n.language)}/packages`}
														replace
													/>
												)
											) : (
												<Navigate to={redirect_to} replace />
											)
										) : (
											<Navigate
												to={`${getRouteLocale(i18n.language)}/auth/login?${qs.stringify({
													redirect_to: redirectToFullPath,
												})}`}
												replace
											/>
										)
									}
								/>
								<Route path="edit">
									<Route
										element={<Navigate to={`${getRouteLocale(i18n.language)}/error`} replace />}
										index
									/>
									<Route
										path=":id"
										element={
											isAuthenticated ? (
												!isDemoTenant && isUserSigned ? (
													hasTeamPageSubscription && canManageTeam ? (
														<UserTeamFormPage pageTitle={t("titles.my_team")} />
													) : (
														<Navigate
															to={`${getRouteLocale(i18n.language)}/packages`}
															replace
														/>
													)
												) : (
													<Navigate to={redirect_to} replace />
												)
											) : (
												<Navigate
													to={`${getRouteLocale(i18n.language)}/auth/login?${qs.stringify({
														redirect_to: redirectToFullPath,
													})}`}
													replace
												/>
											)
										}
									/>
								</Route>
							</Route>
							{canManageJoinRequests ? (
								<Route
									path="join-requests"
									element={
										isAuthenticated ? (
											!isDemoTenant && isUserSigned ? (
												hasTeamPageSubscription ? (
													<UserJoinRequestsPage pageTitle={t("titles.join_requests")} />
												) : (
													<Navigate to={`${getRouteLocale(i18n.language)}/error`} replace />
												)
											) : (
												<Navigate to={redirect_to} replace />
											)
										) : (
											<Navigate
												to={`${getRouteLocale(i18n.language)}/auth/login?${qs.stringify({
													redirect_to: redirectToFullPath,
												})}`}
												replace
											/>
										)
									}
								/>
							) : null}
							<Route path="invoice">
								<Route
									element={<Navigate to={`${getRouteLocale(i18n.language)}/error`} replace />}
									index
								/>
								<Route
									path=":id"
									element={
										isAuthenticated ? (
											!isDemoTenant && isUserSigned ? (
												isUserHasSubscription && isUserOwner ? (
													<UserSubscriptionInvoicePage
														pageTitle={t("titles.print_invoice")}
													/>
												) : (
													<Navigate
														to={
															isUserOwner
																? `${getRouteLocale(i18n.language)}/packages`
																: redirect_to
														}
														replace
													/>
												)
											) : (
												<Navigate to={redirect_to} replace />
											)
										) : (
											<Navigate
												to={`${getRouteLocale(i18n.language)}/auth/login?${qs.stringify({
													redirect_to: redirectToFullPath,
												})}`}
												replace
											/>
										)
									}
								/>
							</Route>
							<Route
								path="patient-intake"
								element={
									isAuthenticated ? (
										!isDemoTenant &&
										isUserSigned &&
										(canManageAccount || canTeamModeratorManageSourceQrCodes) &&
										!isCurrentSegmentLaboratoryAndImagingSegment &&
										(isUserInSubscriptionFreeTrial || isUserHasPatientIntake) ? (
											<PatientIntakePage pageTitle={t("actions.online_patient_intake")} />
										) : (
											<Navigate to={redirect_to} replace />
										)
									) : (
										<Navigate
											to={`${getRouteLocale(i18n.language)}/auth/login?${qs.stringify({
												redirect_to: redirectToFullPath,
											})}`}
											replace
										/>
									)
								}>
								<Route
									element={
										<Navigate
											to={`${getRouteLocale(i18n.language)}/user/patient-intake/list`}
											replace
										/>
									}
									index
								/>
								<Route path="list" element={<PatientIntakeList />}>
									<Route
										element={
											<Navigate
												to={`${getRouteLocale(i18n.language)}/user/patient-intake/list/cases`}
												replace
											/>
										}
										index
									/>
									<Route path="cases" element={<PatientIntakeCases />} />
									<Route path="schedule" element={<PatientIntakeSchedule />} />
									<Route path="pre-booking-consents" element={<PatientIntakePreBookingConsents />} />
									<Route path="booking-fee" element={<PatientIntakeBookingFee />} />
									<Route path="transactions" element={<PatientIntakeTransactions />} />
									<Route path="source-qr-codes" element={<PatientIntakeSourceQrCodes />} />
								</Route>
								<Route path="questions">
									<Route
										element={<Navigate to={`${getRouteLocale(i18n.language)}/error`} replace />}
										index
									/>
									<Route path="edit">
										<Route
											element={<Navigate to={`${getRouteLocale(i18n.language)}/error`} replace />}
											index
										/>
										<Route path=":id" element={<CaseQuestionForm />} />
									</Route>
								</Route>
							</Route>
							<Route
								path="patients"
								element={
									isAuthenticated ? (
										!isDemoTenant && isUserSigned ? (
											isIsolateMembersRestricted ? (
												<Navigate to={`${getRouteLocale(i18n.language)}/error`} replace />
											) : (
												<PatientPage pageTitle={t("actions.patients")} />
											)
										) : (
											<Navigate to={redirect_to} replace />
										)
									) : (
										<Navigate
											to={`${getRouteLocale(i18n.language)}/auth/login?${qs.stringify({
												redirect_to: redirectToFullPath,
											})}`}
											replace
										/>
									)
								}>
								<Route
									element={
										<Navigate to={`${getRouteLocale(i18n.language)}/user/patients/list`} replace />
									}
									index
								/>
								<Route path="list" element={<PatientList />} />
								<Route path="store" element={<PatientForm />} />
								<Route path="edit">
									<Route
										element={<Navigate to={`${getRouteLocale(i18n.language)}/error`} replace />}
										index
									/>
									<Route path=":patient_id" element={<PatientForm />} />
								</Route>
								<Route
									path=":patient_id"
									element={<PatientSingle pageTitle={t("titles.patient_details")} />}>
									<Route
										element={<Navigate to={`${getRouteLocale(i18n.language)}/error`} replace />}
										index
									/>
									<Route path="information" element={<PatientSingleInfo />} />
									<Route path="history" element={<PatientSingleHistory />} />
									{!isCurrentSegmentLaboratoryAndImagingSegment && (
										<Route path="notes" element={<PatientSingleNotesListing />} />
									)}
									{isCurrentCategoryHospitalCategory && (
										<Route path="referrals" element={<PatientSingleReferrals />} />
									)}
									<Route path="orders" element={<PatientSingleLabOrders />} />
									<Route path="documents" element={<DocumentsListing />} />
								</Route>
							</Route>
							<Route
								path="patients"
								element={
									isIsolateMembersRestricted ? (
										<Navigate to={`${getRouteLocale(i18n.language)}/error`} replace />
									) : (
										<Outlet />
									)
								}>
								<Route path=":patient_id">
									<Route
										element={<Navigate to={`${getRouteLocale(i18n.language)}/error`} replace />}
										index
									/>
									<Route path="documents">
										<Route path=":note_id" element={<NotesSinglePage pageTitle="Health Records" />}>
											<Route
												element={
													<Navigate to={`${getRouteLocale(i18n.language)}/error`} replace />
												}
												index
											/>
											<Route
												path="prescription"
												element={<NotesPrintPrescription pageTitle="print prescription" />}
											/>
										</Route>
									</Route>
								</Route>
							</Route>
							<Route path="electronic-form">
								<Route
									element={<Navigate to={`${getRouteLocale(i18n.language)}/error`} replace />}
									index
								/>
								<Route
									path="orders"
									element={
										isAuthenticated ? (
											!isGlobalLoaded ||
											(isGlobalLoaded && isCurrentSegmentLaboratoryAndImagingSegment) ? (
												<OrdersPage pageTitle="Electronic orders" />
											) : (
												<Navigate to={redirect_to} replace />
											)
										) : (
											<Navigate
												to={`${getRouteLocale(i18n.language)}/auth/login?${qs.stringify({
													redirect_to: redirectToFullPath,
												})}`}
												replace
											/>
										)
									}>
									<Route
										element={
											<Navigate
												to={`${getRouteLocale(i18n.language)}/user/electronic-form/orders/list`}
												replace
											/>
										}
										index
									/>
									<Route path="list" element={<ElectronicFormOrders />} />
									<Route path="store" element={<OrderForm />} />
									<Route path="edit">
										<Route
											element={<Navigate to={`${getRouteLocale(i18n.language)}/error`} replace />}
											index
										/>
										<Route path=":order_id" element={<OrderForm />} />
									</Route>
									<Route
										path="one-time"
										element={
											<SubscriptionsOneTimePage
												pageTitle="New Electronic order"
												isElectronicFormOrder
											/>
										}
									/>
								</Route>
							</Route>
							<Route
								path="connections"
								element={
									isAuthenticated ? (
										!isGlobalLoaded || (isGlobalLoaded && isCurrentSegmentPatientSegment) ? (
											<ConnectionsPage pageTitle={t("titles.healthcare_providers")} />
										) : (
											<Navigate to={redirect_to} replace />
										)
									) : (
										<Navigate
											to={`${getRouteLocale(i18n.language)}/auth/login?${qs.stringify({
												redirect_to: redirectToFullPath,
											})}`}
											replace
										/>
									)
								}>
								<Route
									element={
										<Navigate
											to={`${getRouteLocale(i18n.language)}/user/connections/list`}
											replace
										/>
									}
									index
								/>
								<Route path="list" element={<ConnectionsList />} />
							</Route>
							<Route
								path="lab-and-imaging"
								element={
									isAuthenticated ? (
										!isGlobalLoaded || (isGlobalLoaded && isCurrentSegmentMedicalSegment) ? (
											<LabAndImagingPage
												pageTitle={
													isCurrentCategoryHospitalCategory ? "Referrals / Orders" : "Orders"
												}
											/>
										) : (
											<Navigate to={redirect_to} replace />
										)
									) : (
										<Navigate
											to={`${getRouteLocale(i18n.language)}/auth/login?${qs.stringify({
												redirect_to: redirectToFullPath,
											})}`}
											replace
										/>
									)
								}>
								<Route
									element={
										<Navigate
											to={`${getRouteLocale(i18n.language)}/user/lab-and-imaging/${
												isCurrentCategoryHospitalCategory ? "referrals" : "orders"
											}`}
											replace
										/>
									}
									index
								/>
								{isCurrentCategoryHospitalCategory && (
									<Route path="referrals" element={<LabAndImagingReferrals />} />
								)}
								<Route path="orders" element={<LabAndImagingOrdersSubmissions />} />
								<Route path="drafts" element={<LabAndImagingDrafts />}>
									<Route
										element={
											<Navigate
												to={`${getRouteLocale(i18n.language)}/user/lab-and-imaging/drafts/${
													isCurrentCategoryHospitalCategory ? "referrals" : "orders"
												}`}
												replace
											/>
										}
										index
									/>
									{isCurrentCategoryHospitalCategory && (
										<Route path="referrals" element={<LabAndImagingDraftsReferrals />} />
									)}
									<Route path="orders" element={<LabAndImagingDraftsOrders />} />
								</Route>
							</Route>
							<Route
								path="lab-and-imaging/orders/:note_id"
								element={
									isAuthenticated ? (
										!isGlobalLoaded || (isGlobalLoaded && isCurrentSegmentMedicalSegment) ? (
											<NotesSinglePage
												pageTitle="Orders"
												isElectronicFormOrder
												isElectronicFormOrderSubmissions
											/>
										) : (
											<Navigate to={redirect_to} replace />
										)
									) : (
										<Navigate
											to={`${getRouteLocale(i18n.language)}/auth/login?${qs.stringify({
												redirect_to: redirectToFullPath,
											})}`}
											replace
										/>
									)
								}>
								<Route
									element={<NotesSingleInfo isElectronicFormOrder isElectronicFormOrderSubmissions />}
									index
								/>
								<Route
									path="prescription"
									element={<NotesPrintPrescription pageTitle="print order" isElectronicFormOrder />}
								/>
							</Route>
							<Route
								path="lab-and-imaging/referrals/:note_id"
								element={
									isAuthenticated ? (
										!isGlobalLoaded ||
										(isGlobalLoaded &&
											isCurrentSegmentMedicalSegment &&
											isCurrentCategoryHospitalCategory) ? (
											<NotesSinglePage pageTitle="Referrals" isReferral />
										) : (
											<Navigate to={redirect_to} replace />
										)
									) : (
										<Navigate
											to={`${getRouteLocale(i18n.language)}/auth/login?${qs.stringify({
												redirect_to: redirectToFullPath,
											})}`}
											replace
										/>
									)
								}>
								<Route element={<NotesSingleInfo isReferral />} index />
								<Route
									path="prescription"
									element={<NotesPrintPrescription pageTitle="print prescription" isReferral />}
								/>
							</Route>
							<Route path="branches">
								<Route
									element={
										isAuthenticated ? (
											!isDemoTenant && isUserSigned ? (
												<BranchesListingPage pageTitle={t("titles.branches")} />
											) : (
												<Navigate to={redirect_to} replace />
											)
										) : (
											<Navigate
												to={`${getRouteLocale(i18n.language)}/auth/login?${qs.stringify({
													redirect_to: redirectToFullPath,
												})}`}
												replace
											/>
										)
									}
									index
								/>
								<Route
									path="store"
									element={
										isAuthenticated ? (
											!isDemoTenant && isUserSigned ? (
												<BranchesFormPage pageTitle={t("titles.branches")} />
											) : (
												<Navigate to={redirect_to} replace />
											)
										) : (
											<Navigate
												to={`${getRouteLocale(i18n.language)}/auth/login?${qs.stringify({
													redirect_to: redirectToFullPath,
												})}`}
												replace
											/>
										)
									}
								/>
								<Route path="edit">
									<Route
										element={<Navigate to={`${getRouteLocale(i18n.language)}/error`} replace />}
										index
									/>
									<Route
										path=":id"
										element={
											isAuthenticated ? (
												!isDemoTenant && isUserSigned ? (
													<BranchesFormPage pageTitle={t("titles.branches")} />
												) : (
													<Navigate to={redirect_to} replace />
												)
											) : (
												<Navigate
													to={`${getRouteLocale(i18n.language)}/auth/login?${qs.stringify({
														redirect_to: redirectToFullPath,
													})}`}
													replace
												/>
											)
										}
									/>
								</Route>
							</Route>
							<Route
								path="medical-history"
								element={
									isAuthenticated ? (
										!isDemoTenant && isUserSigned ? (
											<MedicalHistoryPage pageTitle={t("actions.my_medical_profile")} />
										) : (
											<Navigate to={redirect_to} replace />
										)
									) : (
										<Navigate
											to={`${getRouteLocale(i18n.language)}/auth/login?${qs.stringify({
												redirect_to: redirectToFullPath,
											})}`}
											replace
										/>
									)
								}
							/>
							<Route
								path="reports-and-prescriptions"
								element={
									isAuthenticated ? (
										!isGlobalLoaded || (isGlobalLoaded && isCurrentSegmentPatientSegment) ? (
											<ReportsAndPrescriptionsPage pageTitle="Health Records" />
										) : (
											<Navigate to={redirect_to} replace />
										)
									) : (
										<Navigate
											to={`${getRouteLocale(i18n.language)}/auth/login?${qs.stringify({
												redirect_to: redirectToFullPath,
											})}`}
											replace
										/>
									)
								}>
								<Route
									element={
										<Navigate
											to={`${getRouteLocale(
												i18n.language
											)}/user/reports-and-prescriptions/documents`}
											replace
										/>
									}
									index
								/>
								<Route path="documents" element={<DocumentsListing />} />
								<Route path="shared" element={<ReportsAndPrescriptionsShared />} />
							</Route>
							<Route path="reports-and-prescriptions">
								<Route path="documents">
									<Route path=":note_id" element={<NotesSinglePage pageTitle="Health Records" />}>
										<Route
											element={<Navigate to={`${getRouteLocale(i18n.language)}/error`} replace />}
											index
										/>
										<Route
											path="prescription"
											element={<NotesPrintPrescription pageTitle="print prescription" />}
										/>
									</Route>
								</Route>
							</Route>
							<Route path="documents" element={<DocumentsPage pageTitle={t("titles.documents")} />} />
							<Route
								path="appointments"
								element={
									isAuthenticated ? (
										!isGlobalLoaded || (isGlobalLoaded && isCurrentSegmentPatientSegment) ? (
											<AppointmentsPage pageTitle="Appointments" />
										) : (
											<Navigate to={redirect_to} replace />
										)
									) : (
										<Navigate
											to={`${getRouteLocale(i18n.language)}/auth/login?${qs.stringify({
												redirect_to: redirectToFullPath,
											})}`}
											replace
										/>
									)
								}>
								<Route element={<AppointmentsListing />} index />
								<Route path=":id" element={<AppointmentsSingle />} />
								<Route path="store" element={<PatientAppointmentForm />} />
								<Route path="edit">
									<Route
										element={<Navigate to={`${getRouteLocale(i18n.language)}/error`} replace />}
										index
									/>
									<Route path=":id" element={<PatientAppointmentForm />} />
								</Route>
							</Route>
							<Route
								path="tasks"
								element={
									isAuthenticated ? (
										isTaskManagementEnabled ? (
											<TasksPage pageTitle={t("actions.assigned_tasks")} />
										) : (
											<Navigate to={redirect_to} replace />
										)
									) : (
										<Navigate
											to={`${getRouteLocale(i18n.language)}/auth/login?${qs.stringify({
												redirect_to: redirectToFullPath,
											})}`}
											replace
										/>
									)
								}>
								<Route element={<TasksListing />} index />
								<Route path=":id" element={<TasksSingle />} />
							</Route>
							<Route
								path="notifications"
								element={
									isAuthenticated ? (
										isNotificationsEnabled ? (
											<NotificationsPage pageTitle={t("actions.inbox")} />
										) : (
											<Navigate to={redirect_to} replace />
										)
									) : (
										<Navigate
											to={`${getRouteLocale(i18n.language)}/auth/login?${qs.stringify({
												redirect_to: redirectToFullPath,
											})}`}
											replace
										/>
									)
								}
							/>
						</Route>
						<Route path="notes">
							<Route
								element={
									isAuthenticated ? (
										!isDemoTenant &&
										isUserSigned &&
										(!isGlobalLoaded ||
											(isGlobalLoaded &&
												(isCurrentSegmentMedicalSegment ||
													isCurrentSegmentPharmacySegment))) ? (
											(isUserHasSubscription && isUserOwner) || !isUserOwner ? (
												<NotesListingPage pageTitle={t("actions.schedule")} />
											) : (
												<Navigate to={`${getRouteLocale(i18n.language)}/packages`} replace />
											)
										) : (
											<Navigate to={`${getRouteLocale(i18n.language)}/error`} replace />
										)
									) : (
										<Navigate
											to={`${getRouteLocale(i18n.language)}/auth/login?${qs.stringify({
												redirect_to: redirectToFullPath,
											})}`}
											replace
										/>
									)
								}
								index
							/>
							<Route path="referral">
								<Route
									element={
										vars.app.allowReferralLetter && +globals?.segment?.referral_letter ? (
											isAuthenticated ? (
												!isDemoTenant && isUserSigned ? (
													(isUserHasSubscription && isUserOwner) || isUserTypeProfessional ? (
														<NotesReferralPage pageTitle={t("actions.schedule")} />
													) : (
														<Navigate
															to={`${getRouteLocale(i18n.language)}/packages`}
															replace
														/>
													)
												) : (
													<Navigate to={redirect_to} replace />
												)
											) : (
												<Navigate
													to={`${getRouteLocale(i18n.language)}/auth/login?${qs.stringify({
														redirect_to: redirectToFullPath,
													})}`}
													replace
												/>
											)
										) : (
											<Navigate to={`${getRouteLocale(i18n.language)}/error`} replace />
										)
									}
									index
								/>
								{vars.app.allowReferralLetter && +globals?.segment?.referral_letter && (
									<Route
										path="print"
										element={
											isAuthenticated ? (
												!isDemoTenant && isUserSigned ? (
													(isUserHasSubscription && isUserOwner) || isUserTypeProfessional ? (
														<NotesReferralPrintPage
															pageTitle={t("titles.referral_letter")}
														/>
													) : (
														<Navigate
															to={`${getRouteLocale(i18n.language)}/packages`}
															replace
														/>
													)
												) : (
													<Navigate to={redirect_to} replace />
												)
											) : (
												<Navigate
													to={`${getRouteLocale(i18n.language)}/auth/login?${qs.stringify({
														redirect_to: redirectToFullPath,
													})}`}
													replace
												/>
											)
										}
									/>
								)}
							</Route>
							<Route
								path="store"
								element={
									isAuthenticated ? (
										!isDemoTenant &&
										isUserSigned &&
										((isUserHasSubscription && isUserOwner) || !isUserOwner) ? (
											!isUserReachesHisNotesLimit ? (
												<NotesStorePage pageTitle={t("actions.create_new_assessment")} />
											) : (
												<Navigate to={`${getRouteLocale(i18n.language)}/error`} replace />
											)
										) : (
											<Navigate to={redirect_to} replace />
										)
									) : (
										<Navigate
											to={`${getRouteLocale(i18n.language)}/auth/login?${qs.stringify({
												redirect_to: redirectToFullPath,
											})}`}
											replace
										/>
									)
								}
							/>
							<Route
								path=":note_id"
								element={
									isAuthenticated ? (
										!isDemoTenant && isUserSigned ? (
											(isUserHasSubscription && isUserOwner) || !isUserOwner ? (
												<NotesSinglePage pageTitle={t("actions.note")} />
											) : (
												<Navigate to={`${getRouteLocale(i18n.language)}/packages`} replace />
											)
										) : (
											<Navigate to={redirect_to} replace />
										)
									) : (
										<Navigate
											to={`${getRouteLocale(i18n.language)}/auth/login?${qs.stringify({
												redirect_to: redirectToFullPath,
											})}`}
											replace
										/>
									)
								}>
								<Route element={<NotesSingleInfo />} index />
								<Route path="logs" element={<NotesSingleLogs />} />
								<Route
									path="prescription"
									element={<NotesPrintPrescription pageTitle="print prescription" />}
								/>
								<Route path="all-documents" element={<NotesAllDocuments pageTitle="All Documents" />} />
							</Route>
							<Route path="edit">
								<Route
									element={<Navigate to={`${getRouteLocale(i18n.language)}/error`} replace />}
									index
								/>
								<Route
									path=":note_id"
									element={
										isAuthenticated ? (
											!isDemoTenant && isUserSigned ? (
												(isUserHasSubscription && isUserOwner) || !isUserOwner ? (
													<NotesDetailsPage pageTitle={t("titles.note")} />
												) : (
													<Navigate
														to={`${getRouteLocale(i18n.language)}/packages`}
														replace
													/>
												)
											) : (
												<Navigate to={redirect_to} replace />
											)
										) : (
											<Navigate
												to={`${getRouteLocale(i18n.language)}/auth/login?${qs.stringify({
													redirect_to: redirectToFullPath,
												})}`}
												replace
											/>
										)
									}>
									<Route
										element={<Navigate to={`${getRouteLocale(i18n.language)}/error`} replace />}
										index
									/>
									<Route path={vars.app.soapSections.subjective} element={<NoteSubjective />} />
									<Route path={vars.app.soapSections.objective} element={<NoteObjective />} />
									<Route path={vars.app.soapSections.assessments} element={<NoteAssessment />} />
									<Route path={vars.app.soapSections.plan} element={<NotePlan />} />
									<Route path={vars.app.soapSections.final} element={<NoteFinal />} />
								</Route>
							</Route>
							<Route path="p">
								<Route
									element={<Navigate to={`${getRouteLocale(i18n.language)}/error`} replace />}
									index
								/>
								<Route
									path=":note_id"
									element={
										!isAuthenticated || canAccessSharedQuestionnaireNote ? (
											<NotesPatientPage pageTitle={t("titles.patient_questionnaire")} />
										) : (
											<Navigate to={redirect_to} replace />
										)
									}>
									<Route element={<NotesPatientValidationPage />} index />
									<Route path="questions" element={<NotesPatientQuestionsPage />} />
									<Route path="finish" element={<NotesPatientFinishPage />} />
								</Route>
							</Route>
						</Route>
						<Route path="orders">
							<Route
								element={
									isAuthenticated ? (
										!isDemoTenant &&
										isUserSigned &&
										(!isGlobalLoaded ||
											(isGlobalLoaded && isCurrentSegmentLaboratoryAndImagingSegment)) ? (
											<NotesListingPage
												pageTitle={t("actions.orders_submissions")}
												isElectronicFormOrder
											/>
										) : (
											<Navigate to={`${getRouteLocale(i18n.language)}/error`} replace />
										)
									) : (
										<Navigate
											to={`${getRouteLocale(i18n.language)}/auth/login?${qs.stringify({
												redirect_to: redirectToFullPath,
											})}`}
											replace
										/>
									)
								}
								index
							/>
							<Route
								path="store"
								element={
									isAuthenticated ? (
										!isDemoTenant && isUserSigned ? (
											<NotesStorePage
												pageTitle={t("titles.create_new_order")}
												isElectronicFormOrder
											/>
										) : (
											<Navigate to={redirect_to} replace />
										)
									) : (
										<Navigate
											to={`${getRouteLocale(i18n.language)}/auth/login?${qs.stringify({
												redirect_to: redirectToFullPath,
											})}`}
											replace
										/>
									)
								}
							/>
							<Route
								path=":note_id"
								element={
									isAuthenticated ? (
										!isDemoTenant &&
										isUserSigned &&
										(!isGlobalLoaded ||
											(isGlobalLoaded && isCurrentSegmentLaboratoryAndImagingSegment)) ? (
											<NotesSinglePage
												pageTitle={t("actions.orders_submissions")}
												isElectronicFormOrder
											/>
										) : (
											<Navigate to={redirect_to} replace />
										)
									) : (
										<Navigate
											to={`${getRouteLocale(i18n.language)}/auth/login?${qs.stringify({
												redirect_to: redirectToFullPath,
											})}`}
											replace
										/>
									)
								}>
								<Route element={<NotesSingleInfo isElectronicFormOrder />} index />
							</Route>
							<Route path="edit">
								<Route
									element={<Navigate to={`${getRouteLocale(i18n.language)}/error`} replace />}
									index
								/>
								<Route
									path=":note_id"
									element={
										isAuthenticated ? (
											!isDemoTenant && isUserSigned ? (
												<NotesDetailsPage title="orders" isElectronicFormOrder />
											) : (
												<Navigate to={redirect_to} replace />
											)
										) : (
											<Navigate
												to={`${getRouteLocale(i18n.language)}/auth/login?${qs.stringify({
													redirect_to: redirectToFullPath,
												})}`}
												replace
											/>
										)
									}>
									<Route
										element={<Navigate to={`${getRouteLocale(i18n.language)}/error`} replace />}
										index
									/>
									<Route
										path={vars.app.soapSections.subjective}
										element={<NoteSubjective isElectronicFormOrder />}
									/>
								</Route>
							</Route>
						</Route>
						<Route path="referrals">
							<Route
								path="store"
								element={
									isAuthenticated ? (
										!isDemoTenant &&
										isUserSigned &&
										(!isGlobalLoaded ||
											(isGlobalLoaded &&
												isCurrentSegmentMedicalSegment &&
												isCurrentCategoryHospitalCategory)) ? (
											<NotesStorePage pageTitle={t("titles.create_new_referral")} isReferral />
										) : (
											<Navigate to={redirect_to} replace />
										)
									) : (
										<Navigate
											to={`${getRouteLocale(i18n.language)}/auth/login?${qs.stringify({
												redirect_to: redirectToFullPath,
											})}`}
											replace
										/>
									)
								}
							/>
							<Route path="edit">
								<Route
									element={<Navigate to={`${getRouteLocale(i18n.language)}/error`} replace />}
									index
								/>
								<Route
									path=":note_id"
									element={
										isAuthenticated ? (
											!isDemoTenant && isUserSigned ? (
												<NotesDetailsPage title={t("actions.referrals")} isReferral />
											) : (
												<Navigate to={redirect_to} replace />
											)
										) : (
											<Navigate
												to={`${getRouteLocale(i18n.language)}/auth/login?${qs.stringify({
													redirect_to: redirectToFullPath,
												})}`}
												replace
											/>
										)
									}>
									<Route
										element={<Navigate to={`${getRouteLocale(i18n.language)}/error`} replace />}
										index
									/>
									<Route
										path={vars.app.soapSections.subjective}
										element={<NoteSubjective isReferral />}
									/>
								</Route>
							</Route>
						</Route>
						{!isAuthenticated && (
							<Route path="pdf">
								<Route
									element={<Navigate to={`${getRouteLocale(i18n.language)}/error`} replace />}
									index
								/>
								<Route
									path=":note_id"
									element={<ElectronicFormOrderPdf pageTitle="Download order" />}
								/>
							</Route>
						)}
						<Route path="electronic-form">
							<Route element={<Navigate to={`${getRouteLocale(i18n.language)}/error`} replace />} index />
							<Route
								path="one-time"
								element={
									<SubscriptionsOneTimePage pageTitle="New Electronic order" isElectronicFormOrder />
								}
							/>
						</Route>
						<Route
							path="patient-intake"
							element={
								<PublicPatientIntakePublicRouteGate pageTitle={t("actions.online_patient_intake")} />
							}>
							<Route element={<PublicPatientIntakeProfile />} index />
							<Route path="start" element={<PublicPatientIntakeStart />} />
							<Route path="choose" element={<PublicPatientIntakeChoose />} />
							<Route path="store" element={<PublicPatientIntakeStore />} />
							<Route path=":note_id">
								<Route path="cases" element={<PublicPatientIntakeCases />} />
								<Route path="questions" element={<PublicPatientIntakeQuestions />} />
								<Route path="patient" element={<PublicPatientIntakePatient />} />
								<Route path="payment">
									<Route element={<PublicPatientIntakePayment />} index />
									<Route path="return" element={<PublicPatientIntakePaymentReturn />} />
								</Route>
								<Route path="finish" element={<NotesPatientFinishPage isPatientIntake />} />
							</Route>
						</Route>
						<Route path="support">
							<Route element={<Navigate to={`${getRouteLocale(i18n.language)}/error`} replace />} index />
							<Route path="contact-us" element={<ContactUsPage pageTitle={t("titles.contacts")} />} />
						</Route>
						<Route path="page">
							<Route element={<Navigate to={`${getRouteLocale(i18n.language)}/error`} replace />} index />
							<Route path=":slug" element={<StaticPage pageTitle={t("titles.static_page")} />} />
						</Route>
						<Route
							path="about-us"
							element={
								!isAuthenticated ? (
									<AboutUsPage pageTitle={t("titles.about_us")} />
								) : (
									<Navigate to={redirect_to} replace />
								)
							}
						/>
						<Route
							path="careers"
							element={
								!isAuthenticated ? (
									<CareersPage pageTitle={t("titles.careers")} />
								) : (
									<Navigate to={redirect_to} replace />
								)
							}
						/>
						<Route
							path="pricing"
							element={
								!isEgyptianGuest ? (
									!isAuthenticated ? (
										<PricingPage pageTitle={t("titles.pricing")} />
									) : (
										<Navigate to={redirect_to} replace />
									)
								) : (
									<Navigate to={`${getRouteLocale(i18n.language)}/error`} replace />
								)
							}
						/>
						<Route path="blog">
							<Route
								element={
									!isAuthenticated ? (
										<BlogListingPage pageTitle={t("titles.blog")} />
									) : (
										<Navigate to={redirect_to} replace />
									)
								}
								index
							/>
							<Route path=":slug" element={<BlogSinglePage pageTitle={t("titles.blog")} />} />
						</Route>
						<Route path="newsletter" element={<NewsletterPage pageTitle={t("titles.newsletter")} />} />
						<Route path="maintenance" element={<MaintenancePage pageTitle={t("titles.coming_soon")} />} />
						<Route path="demo">
							<Route
								element={<Navigate to={`${getRouteLocale(i18n.language)}/demo/start`} replace />}
								index
							/>
							<Route path="start" element={<StartDemoPage pageTitle={t("titles.start_a_demo_note")} />} />
							<Route path="notes">
								<Route path="edit">
									<Route
										element={<Navigate to={`${getRouteLocale(i18n.language)}/error`} replace />}
										index
									/>
									<Route
										path=":note_id"
										element={<NotesDetailsPage pageTitle={t("titles.note")} isDemo />}>
										<Route
											element={<Navigate to={`${getRouteLocale(i18n.language)}/error`} replace />}
											index
										/>
										<Route
											path={vars.app.soapSections.subjective}
											element={<NoteSubjective isDemo />}
										/>
										<Route
											path={vars.app.soapSections.objective}
											element={<NoteObjective isDemo />}
										/>
										<Route
											path={vars.app.soapSections.assessments}
											element={<NoteAssessment isDemo />}
										/>
										<Route path={vars.app.soapSections.plan} element={<NotePlan isDemo />} />
										<Route path={vars.app.soapSections.final} element={<NoteFinal isDemo />} />
									</Route>
								</Route>
								<Route
									path=":note_id"
									element={<NotesSinglePage pageTitle={t("actions.note")} isDemo />}>
									<Route
										element={<Navigate to={`${getRouteLocale(i18n.language)}/error`} replace />}
										index
									/>
									<Route
										path="prescription"
										element={<NotesPrintPrescription pageTitle="print prescription" isDemo />}
									/>
								</Route>
							</Route>
							<Route
								path="finish"
								element={
									<FinishDemoPage
										pageTitle={t("titles.thank_you_for_trying_app", { name: vars.app.name })}
									/>
								}
							/>
						</Route>
						<Route path="error" element={<ErrPage />} />
						<Route
							path="*"
							element={
								isDefaultLocale(i18n.language) &&
								pathname.split("/").filter((local) => vars.app.locales.includes(local))?.length ? (
									<Navigate
										to={`${pathname
											.split("/")
											.filter((locale) => !vars.app.locales.includes(locale))
											.join("/")}${search}${hash}`}
										replace
									/>
								) : (
									<ErrPage />
								)
							}
						/>
					</>
				)}
			</Route>
			<Route
				path="*"
				element={
					<Navigate
						to={`${getRouteLocale(i18n.language) || "/"}${pathname
							.split("/")
							.filter((locale) => !vars.app.locales.includes(locale))
							.join("/")}${search}${hash}`}
						replace
					/>
				}
			/>
		</Routes>
	);
};

export default Router;
